import { FaPlus } from "react-icons/fa";
import { MdTune } from "react-icons/md";
import theme from "../theme";
import { Badge } from "../components/Badge";
import { Box } from "../components/Box";
import { Button } from "../components/Button";
import { Flex } from "../components/Flex";
import { Input } from "../components/Input";
import Topbar from "../components/Topbar";
import CustomTabs from "../components/CustomTabs";
import { Paragraph } from "../components/Paragraph";
import { MonitoringTable } from "../components/MonitoringTable";
import { MonitoringDateFilters } from "../components/monitoringDateFilters";
import {
  HiOutlineSquares2X2,
  HiOutlineUser,
  HiOutlineBuildingOffice2,
} from "react-icons/hi2";
import { ROUTE } from "../routes";
import { env } from "../utils/env";
import { FormEvent, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClickOutside } from "../hooks/useClickOutside";
import { useMonitoringStatus } from "../data/useMonitoringStatus";
import { useDashboardView } from "../hooks/useDashboardView";
import { monitoringView } from "../hooks/dashboardViews/monitoring";
import { ActiveFilterBar, getAppliedFilterCount } from "../components/ActiveFilterBar";
import { PAGE_SIZE_OPTIONS } from "../components/ServerTable";
import { useAuth } from "../context/AuthContext";

const Monitoring = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // -- Topbar/search/dropdown state
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);

  const handleSelection = (type: "individual" | "company") => {
    setIsDropdownOpen(false);
    if (type === "individual") {
      navigate(ROUTE.MONITORING_NEW);
    } else {
      navigate(ROUTE.MONITORING_NEW_COMPANY);
    }
  };

  useClickOutside(dropdownRef, isDropdownOpen, closeDropdown);

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const { data: statusData } = useMonitoringStatus();
  const statusOptions = statusData?.data?.map(({ id, status }) => ({
    value: id,
    label: `${status}`,
  }));

  const optionLists = useMemo(
    () => ({
      statuses: statusOptions ?? [],
      pageSizes: PAGE_SIZE_OPTIONS,
    }),
    [statusOptions],
  );

  const optionListsReady = Boolean(statusData?.data);

  const {
    view,
    setValue,
    setValues,
    clearValue,
    clearAll,
    appliedKeys,
  } = useDashboardView(monitoringView, { optionLists, optionListsReady });

  const activeTab = view.tab as number;
  const submittedSearchTerm = view.search as string;

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    setValue("search", searchTerm);
  };
  const handleResetSearch = () => {
    setSearchTerm("");
    setValue("search", "");
  };

  const dateFilters = useMemo(
    () => ({
      status: view.status,
      created_on_date: [view.createdOnFrom, view.createdOnTo],
      last_activity_date: [view.lastActivityFrom, view.lastActivityTo],
    }),
    [view.status, view.createdOnFrom, view.createdOnTo, view.lastActivityFrom, view.lastActivityTo],
  );

  const appliedFilterCount = useMemo(
    () => getAppliedFilterCount(appliedKeys, monitoringView),
    [appliedKeys],
  );

  // Chips render below the table (see ActiveFilterBar below), so the
  // table's own reserved height needs to shrink to make room for that row -
  // otherwise it overlaps the fixed-position pagination bar under the table.
  const hasFilterChips = appliedKeys.some((key) => monitoringView.fields[key]?.chipLabel);

  const handleDateFilterSubmit = (values) => {
    setValues({
      status: values.status,
      createdOnFrom: values.created_on_date?.[0] ?? null,
      createdOnTo: values.created_on_date?.[1] ?? null,
      lastActivityFrom: values.last_activity_date?.[0] ?? null,
      lastActivityTo: values.last_activity_date?.[1] ?? null,
    });
    setIsFiltersOpen(false);
  };

  const tabTypes = ["", "individual", "entity"];
  const tabLabels = [
    (
      <>
        <HiOutlineSquares2X2 size={16} />
        All
      </>
    ),
    (
      <>
        <HiOutlineUser size={16} />
        Individual
      </>
    ),
    (
      <>
        <HiOutlineBuildingOffice2 size={16} />
        Entity
      </>
    ),
  ];

  return (
    <>
      <Topbar title="Monitoring" noBorder>
        <Flex justifyContent="space-between" width="100%">
          {env.isSearchMonitoringFeatureOn ? (
            <Flex as="form" ml={4} onSubmit={handleSearchSubmit}>
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                type="search"
                size="small"
                placeholder="Search"
                maxWidth={250}
                mr={1}
              />
              <Button
                isDisabled={submittedSearchTerm === searchTerm}
                type="submit"
              >
                Search
              </Button>
              {submittedSearchTerm && (
                <Button
                  variant="text"
                  pl="0"
                  ml={1}
                  onClick={handleResetSearch}
                >
                  Reset
                </Button>
              )}
            </Flex>
          ) : (
            <Box />
          )}

          <Flex alignItems="center">
            <Button
              variant="secondary"
              mr={1}
              Icon={MdTune}
              iconPosition="right"
              onClick={() => setIsFiltersOpen((o) => !o)}
            >
              Filter
              {appliedFilterCount > 0 && <Badge ml={1}>{appliedFilterCount}</Badge>}
            </Button>

            <Flex position="relative" ref={dropdownRef}>
              <Button
                Icon={FaPlus}
                iconSize="16px"
                onClick={() => setIsDropdownOpen((o) => !o)}
                isDisabled={!hasPermission("monitoring.create")}
                title={
                  hasPermission("monitoring.create")
                    ? undefined
                    : "You don't have permission to create monitoring records"
                }
              >
                New Monitoring Record
              </Button>

              {isDropdownOpen && (
                <Box
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    background: theme.colors.white,
                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                    borderRadius: 12,
                    zIndex: 10,
                    width: 200,
                  }}
                >
                  <Paragraph
                    my={0}
                    px={3}
                    py={2}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSelection("individual")}
                  >
                    Individual
                  </Paragraph>
                  <Paragraph
                    my={0}
                    px={3}
                    py={2}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSelection("company")}
                  >
                    Company
                  </Paragraph>
                </Box>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Topbar>

      <CustomTabs
        activeTab={activeTab}
        onTabChange={(index) => setValue("tab", index)}
        customMarginLeft="24px"
        variant="segmented"
        tabs={tabTypes.map((type, index) => ({
          label: tabLabels[index],
          content: activeTab === index && (
            <>
              <Box px="24px">
                <ActiveFilterBar
                  appliedKeys={appliedKeys}
                  schema={monitoringView}
                  view={view}
                  optionLists={optionLists}
                  onRemove={clearValue}
                  onClearAll={clearAll}
                />
              </Box>
              <MonitoringTable
                searchTerm={submittedSearchTerm}
                filters={dateFilters}
                hasFilterChips={hasFilterChips}
                type={type}
                initialSorting={[{ id: view.sortBy as string, desc: view.sortDesc as boolean }]}
                initialPageSize={view.pageSize as number}
                onSortChange={(sorting) =>
                  setValues({
                    sortBy: sorting[0]?.id ?? "created_on",
                    sortDesc: sorting[0]?.desc ?? true,
                  })
                }
                onPageSizeChange={(pageSize) => setValue("pageSize", pageSize)}
              />

              <MonitoringDateFilters
                isOpen={isFiltersOpen}
                onRequestClose={() => setIsFiltersOpen(false)}
                onSubmit={handleDateFilterSubmit}
                filters={dateFilters}
              />
            </>
          ),
        }))}
      />
    </>
  );
};

export default Monitoring;
